import { Authenticator, CheckboxField, useAuthenticator } from '@aws-amplify/ui-react'
import ArwenLogo from '../../assets/brand/arwen-logo-red-medium.png'
import { useNavigate } from 'react-router-dom'
import { lazy, Suspense, useEffect, useState } from 'react'

type Props = {
    hideSignUp: boolean
    initialState: 'signIn' | 'signUp'
    showHeader: boolean
    onboarding?: boolean
    onTabChange?: (route: string) => void
}

enum Action {
    SignUp = 'signUp',
    SignIn = 'signIn',
    Transition = 'transition',
    Unknown = 'unknown'
}

const mapRouteToAction = (route: string): Action => {
    switch (route) {
        case 'signUp':
            return Action.SignUp
        case 'signIn':
            return Action.SignIn
        case 'transition':
            return Action.Transition
        default:
            return Action.Unknown
    }
}

// Demo mode replaces the real Cognito login with a fake one (DemoLogin) so
// the whole app can be shown running on fictional in-browser data — see
// src/demo/. That code, fixtures included, must never reach clients in real
// builds, and keeping it out relies on this exact `process.env` comparison
// being written as a literal:
//
//   1. At build time CRA's DefinePlugin substitutes the env var's value into
//      the source text. In any build without REACT_APP_DEMO_MODE, the line
//      becomes `undefined === 'true'`.
//   2. Terser sees that's constantly false and deletes the whole branch,
//      including the `import('../../demo/auth/DemoLogin')` call.
//   3. With no import left anywhere, webpack never creates the demo chunk,
//      so the demo code isn't even present as a lazily-loadable file.
//
// All three steps depend on the condition being statically decidable. If you
// refactor this into a runtime flag (e.g. a config.demoMode property, or even
// assigning process.env.REACT_APP_DEMO_MODE to a variable first), the chain
// breaks and the demo bundle ships to every environment — it fails silently,
// with no error and no behaviour change beyond bundle size. If you need to
// change how demo mode is toggled, check the emitted chunks afterwards
// (`yarn build`, then look for demo code in build/static/js/).
const DemoLogin =
    process.env.REACT_APP_DEMO_MODE === 'true'
        ? lazy(() => import('../../demo/auth/DemoLogin'))
        : null

export default function Authenticate(props: Props) {
    if (DemoLogin) {
        return (
            <Suspense fallback={null}>
                <DemoLogin />
            </Suspense>
        )
    }
    return <RealAuthenticate {...props} />
}

function RealAuthenticate(props: Props) {
    const { hideSignUp, initialState, showHeader, onboarding, onTabChange } = props
    const navigate = useNavigate()
    const { route, toSignIn, toSignUp } = useAuthenticator((context) => [context.route])
    const [action, setAction] = useState<Action>(mapRouteToAction(route))

    useEffect(() => {
        initialState === 'signIn' ? toSignIn() : toSignUp()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (!!onTabChange && ['signUp', 'signIn'].includes(route)) {
            onTabChange(route)
        }
    }, [route, onTabChange])

    useEffect(() => setAction(mapRouteToAction(route)), [route])

    useEffect(() => {
        // If we're:
        // 1. at the end of the authentication flow
        // 2. onboarding
        // Then we want to redirect to the new account page, and enable onboarding.
        if (action === Action.Transition && onboarding) {
            // Adding a small timeout to ensure the state is fully updated before redirect
            setTimeout(() => {
                navigate('/account/new?onboarding=true')
            }, 500)
        }
    }, [action, navigate, onboarding])
    return (
        <>
            <Authenticator
                hideSignUp={hideSignUp}
                initialState={initialState}
                formFields={{
                    signIn: {
                        username: {
                            placeholder: 'Email address',
                            label: ''
                        },
                        password: {
                            placeholder: 'Password',
                            label: ''
                        }
                    },
                    signUp: {
                        password: {
                            placeholder: 'Password',
                            label: ''
                        },
                        confirm_password: {
                            placeholder: 'Please confirm your password',
                            label: ''
                        }
                    }
                }}
                components={{
                    Header() {
                        return showHeader ? (
                            <img alt="logo" src={ArwenLogo} className="mb-8 ml-20" width="300" />
                        ) : (
                            <></>
                        )
                    },
                    Footer() {
                        // If we're signing up a user then we don't need to display this because there's a checkbox
                        // they need to check that says the same thing.
                        return action !== Action.SignUp ? (
                            <div className="text-sm text-gray-500 justify-center">
                                <p className="text-center">
                                    By proceeding I agree to Arwen AI Ltd’s{' '}
                                    <a
                                        href="https://www.arwen.ai/eula"
                                        className="text-primary-600 underline"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        EULA
                                    </a>{' '}
                                    and{' '}
                                    <a
                                        href="https://www.arwen.ai/privacy-policy"
                                        className="text-primary-600 underline"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Privacy Policy
                                    </a>
                                </p>
                            </div>
                        ) : (
                            <></>
                        )
                    },
                    SignUp: {
                        FormFields() {
                            const { validationErrors } = useAuthenticator()

                            return (
                                <>
                                    {/* Re-use default `Authenticator.SignUp.FormFields` */}
                                    <Authenticator.SignUp.FormFields />

                                    {/* Add custom fields here */}
                                    <CheckboxField
                                        //@ts-ignore
                                        errorMessage={validationErrors.acknowledgement}
                                        hasError={!!validationErrors.acknowledgement}
                                        name="acknowledgement"
                                        value="yes"
                                        label={
                                            <div>
                                                I accept the{' '}
                                                <a
                                                    href="https://www.arwen.ai/eula"
                                                    className="text-primary-600 underline"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    Arwen EULA.
                                                </a>{' '}
                                                and{' '}
                                                <a
                                                    href="https://www.arwen.ai/privacy-policy"
                                                    className="text-primary-600 underline"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    Privacy Policy
                                                </a>
                                            </div>
                                        }
                                    />
                                </>
                            )
                        }
                    }
                }}
            >
                {({ signOut, user }) => <div />}
            </Authenticator>
        </>
    )
}
